import logger from './logger';
import { RESERVED_COMPUTED_PROPERTY_NAMES, type ComputedProperties } from './ReplicantComputedProperties';
import type { IndexCountFn, IndexSearchFn } from './ReplicantIndexes';
import type { MessageInfo } from './ReplicantMessages';
import type ReplicantMigrator from './ReplicantMigrator';
import type SB from './SchemaBuilder';
import { systemStateFieldNames } from './systemStateFields';
import type { Immutable } from './utils/TypeUtils';

export type SharedStateSystemFields = {
    /** Shared state ID. */
    readonly id: string;

    /** Shared state global item creation timestamp. */
    readonly createdAt: number;

    /**
     * Shared state global item latest update timestamp. Incremented on message post and counter update.
     *
     * The value is rounded down to the latest minute when accessed inside a computed property getter. This is to prevent updating the index too frequently.
     */
    readonly updatedAt: number;
};

export type WithSharedStateSystemFields<T> = T & SharedStateSystemFields;

export const sharedStateSystemFieldNames: (keyof SharedStateSystemFields)[] = ['id', 'createdAt', 'updatedAt'];

//
// Schema

type Schema = {
    /**
     * State schema definition.
     *
     * The following **reserved properties** are exposed automatically in global shared state: `id`, `createdAt` and `updatedAt`.
     */
    schema: SB.Schema;

    migrator?: ReplicantMigrator;
};

// TODO handle counter type inference in createSharedStateSchema instead of requiring `as const`
interface GlobalSchema extends Schema {
    /**
     * A list of counter names.
     *
     * Counter names may include only characters `a-z`, `A-Z` and `0-9`.
     *
     * @see https://docs.dev.gc-internal.net/replicant/basic-features/SharedStates/index.html#counters
     *
     * @example
     * createSharedStateSchema({
     *   global: {
     *     schema: SB.object({ }),
     *     counters: ['memberCount' as const, 'score' as const], // Use `as const` to handle type inference
     *   },
     * })
     */
    counters?: string[];
}

export type SharedStateSchema = {
    global: GlobalSchema;
    user?: Schema;
};

export type SimpleSharedStateSchema = {
    global?: never;
    user: Schema;
};

//
// Messages

export type SharedStateMessageReducer<TSharedSchema extends SharedStateSchema, TArgs> = (
    state: {
        global: WithSharedStateSystemFields<SB.ExtractType<TSharedSchema['global']['schema']>>;
        counters: Immutable<SharedStateCounterValues<TSharedSchema>>;
        users: Immutable<Partial<{ [userId: string]: SB.ExtractType<NonNullable<TSharedSchema['user']>['schema']> }>>;
    },
    args: TArgs,
    info: MessageInfo,
) => void;

export type SharedStateMessage<TSharedSchema extends SharedStateSchema, TArgs = any> = {
    schema: SB.Schema<TArgs>;
    reducer: SharedStateMessageReducer<TSharedSchema, TArgs>;
};

export type SharedStateMessages<TSharedSchema extends SharedStateSchema = SharedStateSchema> = {
    [messageName: string]: SharedStateMessage<TSharedSchema>;
};

//
// State

export type SharedState = {
    schema: SharedStateSchema | SimpleSharedStateSchema;
    messages: SharedStateMessages;
    computedProperties: ComputedProperties;
};

export type SharedStates = {
    [stateName: string]: SharedState;
};

/** The shared states for a single user. */
export type UserSharedStates<TSharedStates extends SharedStates = {}> = {
    [KStateName in keyof TSharedStates]: Partial<{
        [sharedStateId: string]: SB.ExtractType<NonNullable<TSharedStates[KStateName]['schema']['user']>['schema']>;
    }>;
};

//
// Creation helpers

export function createSharedStateSchema<TSchema extends SharedStateSchema | SimpleSharedStateSchema>(schema: TSchema) {
    const globalSchema =
        schema.global?.schema && schema.global.schema.isObjectSchema() ? schema.global.schema : undefined;

    if (globalSchema) {
        const globalSchemaPropNames = Object.keys(globalSchema.getFullSchema());

        for (const propName of globalSchemaPropNames) {
            if (systemStateFieldNames.includes(propName as any)) {
                // Log warning instead of throwing an error to make it easier to migrate old shared states to system state fields:
                logger.warn(
                    `Property name ${propName} in shared state global schema clashes with a reserved keyword. The property value will be overwritten by a system field.`,
                );
            }
        }
    }

    const counterNames = schema.global?.counters;

    if (counterNames) {
        const counterNameRegex = /^[a-zA-Z0-9]+$/;

        for (const counterName of counterNames) {
            if (!counterNameRegex.test(counterName)) {
                throw Error(`Invalid shared state counter name: ${counterName}. Only letters and numbers are allowed.`);
            }
        }
    }

    return schema;
}

export function createSharedStateMessage<TSharedSchema extends SharedStateSchema, TArgs>(
    argsSchema: SB.Schema<TArgs>,
    reducer: SharedStateMessageReducer<TSharedSchema, TArgs>,
): SharedStateMessage<TSharedSchema, TArgs> {
    return { schema: argsSchema, reducer };
}

export function createSharedStateMessages<TSharedSchema extends SharedStateSchema>(sharedSchema: TSharedSchema) {
    return <TMessages extends SharedStateMessages<TSharedSchema>>(messages: TMessages) => messages;
}

export function createSharedStateComputedProperties<
    TGlobalState,
    TUserState = never,
    TCounter extends string = never,
>(sharedSchema: {
    global: { schema: SB.Schema<TGlobalState>; counters?: TCounter[] };
    user?: { schema: SB.Schema<TUserState> };
}) {
    return <
        TComputedProperties extends ComputedProperties<{
            global: WithSharedStateSystemFields<TGlobalState>;
            counters: SharedStateCounterMap<TCounter>;
            users: Partial<{ [userId: string]: TUserState }>;
        }>,
    >(
        computedProperties: TComputedProperties,
    ) => {
        for (const [key, value] of Object.entries(computedProperties)) {
            if (RESERVED_COMPUTED_PROPERTY_NAMES.includes(key)) {
                throw Error(`Cannot use reserved keyword ${key} as computed property name`);
            }

            if (!value._payload && !value._searchable) {
                throw Error(
                    `Invalid shared state computed property ${key}: computed property must be either searchable or payload`,
                );
            }
        }

        return computedProperties;
    };
}

/**
 * Create a shared state configuration.
 *
 * @see https://docs.dev.gc-internal.net/replicant/basic-features/SharedStates
 *
 * @param schema Shared state schema created with `createSharedStateSchema`.
 * @param messages Global shared state messages created with `createSharedStateMessages`.
 * @param computedProperties Global shared state computed properties created with `createSharedStateComputedProperties`.
 * @returns Shared state configuration object, to be passed into `createConfig({ sharedStates: { mySharedState }, ... })`.
 */
export function createSharedState<TSchema extends SimpleSharedStateSchema>(
    schema: TSchema,
): { schema: TSchema; messages: {}; computedProperties: {} };
export function createSharedState<
    TSchema extends SharedStateSchema,
    TMessages extends SharedStateMessages<TSchema> = {},
    TComputedProperties extends ComputedProperties = {},
>(
    schema: TSchema,
    messages?: TMessages,
    computedProperties?: TComputedProperties,
): { schema: TSchema; messages: TMessages; computedProperties: TComputedProperties };
export function createSharedState(
    schema: SharedStateSchema | SimpleSharedStateSchema,
    messages = {},
    computedProperties = {},
) {
    if (!schema.global) {
        if (Object.keys(messages).length > 0) {
            throw Error('Cannot define messages for a shared state without a global schema');
        }

        if (Object.keys(computedProperties).length > 0) {
            throw Error('Cannot define computed properties for a shared state without a global schema');
        }
    }

    return { schema, messages, computedProperties };
}

//
// Internals.

export type PostSharedStatesMessagesMap<TSharedStateMessages extends SharedStateMessages | undefined> =
    TSharedStateMessages extends SharedStateMessages
        ? {
              [KMessageName in keyof TSharedStateMessages]: (
                  sharedStateId: string,
                  args: SB.ExtractType<TSharedStateMessages[KMessageName]['schema']>,
              ) => void;
          }
        : never;

export type SharedStateCounterMap<T extends string = string> = {
    [counterName in T]: {
        /** Counter value. */
        value: number;

        /**
         * Latest counter update timestamp, or `0` if counter was never updated.
         *
         * The value is rounded down to the latest minute when accessed inside a computed property getter. This is to prevent updating the index too frequently.
         */
        updatedAt: number;
    };
};

export type SharedStateCounterValues<T extends SharedStateSchema | SimpleSharedStateSchema> = T extends {
    global: { counters: (infer TCounter extends string)[] };
}
    ? SharedStateCounterMap<TCounter>
    : never;

export type FullSharedState<TSharedState extends SharedState> = {
    global: WithSharedStateSystemFields<SB.ExtractType<NonNullable<TSharedState['schema']['global']>['schema']>>;

    counters: SharedStateCounterValues<TSharedState['schema']>;

    users: TSharedState['schema'] extends { user: { schema: SB.Schema<infer TUserState> } }
        ? Partial<{ [userId: string]: TUserState }>
        : never;
};

export type SharedStateAsyncGetterAPI<TSharedState extends SharedState> = {
    /**
     * @returns A Promise that resolves with the complete shared state instance, or `undefined` if not found.
     */
    fetch: (sharedStateId: string) => Promise<FullSharedState<TSharedState> | undefined>;

    /**
     * @returns A list of global shared states that match the given computed property query.
     *
     * @see https://docs.dev.gc-internal.net/replicant/SharedStates/#indexing
     */
    search: IndexSearchFn<TSharedState['computedProperties']>;

    /**
     * @returns The number of global shared states that match the given computed property query.
     *
     * @see https://docs.dev.gc-internal.net/replicant/SharedStates/#indexing
     */
    count: IndexCountFn<TSharedState['computedProperties']>;
};

export type SharedStateCounterAPI = {
    /**
     * Increase counter value by `increment`. Use a negative `increment` to decrease counter value.
     *
     * Note that the increase is not reflected in any subsequent shared state `.fetch` results in the same action.
     *
     * @param increment A safe integer.
     *
     * @throws If `increment` is not a safe integer.
     */
    increase(sharedStateId: string, increment: number): void;

    /**
     * Replace counter value by `newValue`.
     *
     * Concurrent `.set` calls from multiple users will override each other.
     * **Use `.set` only in cases like migrating an existing non-counter shared state property into a counter**.
     * Regular counter value increments and decrements should use `.increase`.
     *
     * @param newValue A safe integer.
     *
     * @throws If `newValue` is not a safe integer.
     */
    set(sharedStateId: string, newValue: number): void;
};

export type SharedStateCountersAPI<T extends SharedState> = T['schema']['global'] extends {
    counters: (infer TCounter extends string)[];
}
    ? { [counter in TCounter]: SharedStateCounterAPI }
    : never;

export type SharedStateSyncActionAPI<TSharedState extends SharedState> = {
    counters: SharedStateCountersAPI<TSharedState>;

    /**
     * Post a message to mutate global shared state.
     *
     * The available messages and argument types are defined with the `sharedStates` configuration property in Replicant config.
     *
     * @throws If global state hasn't been created yet.
     */
    postMessage: PostSharedStatesMessagesMap<TSharedState['messages']>;

    /**
     * Set the player's own user state value for given shared state.
     *
     * @throws If shared state is configured with global state but global state hasn't been created yet.
     */
    setUserState(
        sharedStateId: string,
        userState: SB.ExtractType<NonNullable<TSharedState['schema']['user']>['schema']>,
    ): void;

    /**
     * Delete player's own user state value for given shared state.
     *
     * @throws If user state value for `sharedStateId` does not exist.
     */
    deleteUserState(sharedStateId: string): void;
};

export type SharedStateAsyncActionAPI<TSharedState extends SharedState> = {
    /**
     * Create a shared state instance.
     *
     * @param customSharedStateId A custom shared state ID. The ID must be unique within this shared state - not all shared states of this app. Leave `undefined` to use an auto-generated ID.
     * @returns Shared state ID.
     * @throws Async action fails with an aggregate error if a shared state with `customSharedStateId` already exists. Check for `{ subCode: 'shared_state_duplicate_id' }` in `error.clientExtras.errors` to handle the error.
     */
    create(customSharedStateId?: string): string;

    /**
     * Fetch a shared state with `sharedStateId`, or create a new one if no shared state with `sharedStateId` exists.
     *
     * @returns A Promise that resolves with the complete shared state instance.
     */
    fetchOrCreate(sharedStateId: string): Promise<FullSharedState<TSharedState>>;
};

export type SharedStatesAsyncGetterAPI<TSharedStates extends SharedStates> = {
    [KStateName in keyof TSharedStates]: SharedStateAsyncGetterAPI<TSharedStates[KStateName]>;
};

export type SharedStatesSyncActionAPI<TSharedStates extends SharedStates> = {
    [KStateName in keyof TSharedStates & string]: SharedStateSyncActionAPI<TSharedStates[KStateName]>;
};

export type SharedStatesAsyncActionAPI<TSharedStates extends SharedStates> = {
    [KStateName in keyof TSharedStates & string]: SharedStateAsyncActionAPI<TSharedStates[KStateName]> &
        SharedStateSyncActionAPI<TSharedStates[KStateName]> &
        SharedStateAsyncGetterAPI<TSharedStates[KStateName]>;
};

export function generateSharedStateId(opts: {
    userId: string;
    stateName: string;
    random: () => number;
    now: () => number;
}) {
    const noise = opts.random().toString(36).substring(2);
    return `${opts.userId}-${opts.stateName}-${noise}-${opts.now()}`;
}

export function sharedStateHasSearchableProperties(sharedState: SharedState): boolean {
    return (
        !!sharedState.schema.global &&
        Object.values(sharedState.computedProperties || {}).some((computedProperty) => computedProperty._searchable)
    );
}
